(function(root, factory) {
  'use strict';

  var controller = factory();
  if (typeof module === 'object' && module.exports) module.exports = controller;
  if (root) root.EnigmaCustomerPrivacy = controller;
  if (root && root.document) controller.bind(root, root.document);
})(typeof window !== 'undefined' ? window : null, function() {
  'use strict';

  var TRIGGER_SELECTOR = '[data-enigma-cookie-preferences]';
  var PREFERENCES_DIALOG_ID = 'shopify-pc__prefs__dialog';
  var PREFERENCES_WAIT_MS = 8000;
  var PREFERENCES_POLL_MS = 100;
  var customerPrivacyRoot = null;
  var customerPrivacyPromise = null;

  function customerPrivacyApiFor(root) {
    if (!root || !root.Shopify) return Promise.resolve(null);
    if (root.Shopify.customerPrivacy) return Promise.resolve(root.Shopify.customerPrivacy);
    if (typeof root.Shopify.loadFeatures !== 'function') return Promise.resolve(null);

    if (customerPrivacyRoot === root && customerPrivacyPromise) return customerPrivacyPromise;
    customerPrivacyRoot = root;
    customerPrivacyPromise = new Promise(function(resolve) {
      try {
        root.Shopify.loadFeatures([
          { name: 'consent-tracking-api', version: '0.1' }
        ], function(error) {
          resolve(error ? null : (root.Shopify.customerPrivacy || null));
        });
      } catch (error) {
        resolve(null);
      }
    });

    return customerPrivacyPromise;
  }

  function preferencesProcessingAllowedSync(root) {
    var api = root && root.Shopify && root.Shopify.customerPrivacy;
    if (!api || typeof api.preferencesProcessingAllowed !== 'function') return false;

    try {
      return api.preferencesProcessingAllowed() === true;
    } catch (error) {
      return false;
    }
  }

  function preferencesProcessingAllowed(root) {
    return customerPrivacyApiFor(root).then(function(api) {
      if (!api || typeof api.preferencesProcessingAllowed !== 'function') return false;
      try {
        return api.preferencesProcessingAllowed() === true;
      } catch (error) {
        return false;
      }
    }, function() {
      return false;
    });
  }

  function whenPreferencesAllowed(root, callback) {
    return preferencesProcessingAllowed(root).then(function(allowed) {
      if (!allowed || typeof callback !== 'function') return false;
      try {
        callback();
        return true;
      } catch (error) {
        if (root && root.console && typeof root.console.error === 'function') {
          root.console.error('[Enigma privacy] Operazione di preferenza non disponibile.', error);
        }
        return false;
      }
    });
  }

  function privacyBannerFor(root) {
    if (root && root.privacyBanner) return root.privacyBanner;
    if (typeof privacyBanner !== 'undefined') return privacyBanner;
    return null;
  }

  function errorElement(documentObject, trigger) {
    if (!documentObject || !trigger || typeof trigger.getAttribute !== 'function') return null;
    var errorId = trigger.getAttribute('aria-describedby');
    return errorId && typeof documentObject.getElementById === 'function'
      ? documentObject.getElementById(errorId)
      : null;
  }

  function setBusy(trigger, busy) {
    if (!trigger) return;
    trigger.disabled = busy;
    if (busy && typeof trigger.setAttribute === 'function') {
      trigger.setAttribute('aria-busy', 'true');
    } else if (typeof trigger.removeAttribute === 'function') {
      trigger.removeAttribute('aria-busy');
    }
  }

  function reportFailure(root, documentObject, trigger, error) {
    var message = errorElement(documentObject, trigger);
    if (message) message.hidden = false;
    if (root && root.console && typeof root.console.error === 'function') {
      root.console.error('[Enigma privacy] Impossibile aprire le preferenze cookie.', error);
    }
  }

  function preferencesDialogVisible(root, documentObject) {
    var dialog = documentObject && typeof documentObject.getElementById === 'function'
      ? documentObject.getElementById(PREFERENCES_DIALOG_ID)
      : null;
    if (!dialog || typeof dialog.getClientRects !== 'function' || !dialog.getClientRects().length) return false;
    for (var element = dialog; element; element = element.parentElement) {
      if (element.hidden || (typeof element.getAttribute === 'function' && element.getAttribute('aria-hidden') === 'true')) return false;
      if (root && typeof root.getComputedStyle === 'function') {
        var style = root.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      }
    }
    return true;
  }

  function waitForPreferencesDialog(root, documentObject) {
    return new Promise(function(resolve) {
      var waited = 0;
      var schedule = root && typeof root.setTimeout === 'function' ? root.setTimeout.bind(root) : setTimeout;
      function check() {
        if (preferencesDialogVisible(root, documentObject)) return resolve(true);
        if (waited >= PREFERENCES_WAIT_MS) return resolve(false);
        waited += PREFERENCES_POLL_MS;
        schedule(check, PREFERENCES_POLL_MS);
      }
      check();
    });
  }

  function clearErrorWhenPreferencesAppear(root, documentObject, trigger) {
    var Observer = root && root.MutationObserver;
    if (!Observer || !documentObject || !documentObject.body) return;
    var observer;
    function observeNativeAncestors() {
      var element = documentObject.getElementById(PREFERENCES_DIALOG_ID);
      while (element && element !== documentObject.body) {
        observer.observe(element, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
        element = element.parentElement;
      }
    }
    observer = new Observer(function() {
      if (preferencesDialogVisible(root, documentObject)) {
        var message = errorElement(documentObject, trigger);
        if (message) message.hidden = true;
        observer.disconnect();
        if (trigger) trigger.__enigmaPreferencesLateObserver = null;
      } else {
        observeNativeAncestors();
      }
    });
    // Native Shopify inserts its wrapper directly under body. Existing hidden
    // wrappers can reopen via an attribute change. Avoid indefinite polling or
    // observing every product/carousel mutation in the page subtree.
    observer.observe(documentObject.body, { childList: true });
    observeNativeAncestors();
    if (trigger) trigger.__enigmaPreferencesLateObserver = observer;
  }

  function openPreferences(root, documentObject, trigger) {
    if (trigger && trigger.__enigmaPreferencesLateObserver) {
      trigger.__enigmaPreferencesLateObserver.disconnect();
      trigger.__enigmaPreferencesLateObserver = null;
    }
    var message = errorElement(documentObject, trigger);
    if (message) message.hidden = true;

    var banner = privacyBannerFor(root);
    if (!banner || typeof banner.showPreferences !== 'function') {
      reportFailure(root, documentObject, trigger, new Error('Shopify privacy banner API unavailable'));
      return Promise.resolve(false);
    }

    setBusy(trigger, true);
    var request;
    try {
      request = banner.showPreferences();
    } catch (error) {
      setBusy(trigger, false);
      reportFailure(root, documentObject, trigger, error);
      return Promise.resolve(false);
    }

    // Shopify can resolve showPreferences before its asynchronous render, and
    // can absorb configuration/network failures internally. Confirm the dialog
    // is actually visible before reporting success to the user.
    return Promise.resolve(request).then(function() {
      return waitForPreferencesDialog(root, documentObject);
    }).then(function(visible) {
      setBusy(trigger, false);
      if (!visible) {
        reportFailure(root, documentObject, trigger, new Error('Shopify privacy preferences did not become visible'));
        clearErrorWhenPreferencesAppear(root, documentObject, trigger);
        return false;
      }
      return true;
    }, function(error) {
      setBusy(trigger, false);
      reportFailure(root, documentObject, trigger, error);
      return false;
    });
  }

  function handleClick(root, documentObject, event) {
    var target = event && event.target;
    var trigger = target && typeof target.closest === 'function'
      ? target.closest(TRIGGER_SELECTOR)
      : null;
    if (!trigger) return null;

    if (typeof event.preventDefault === 'function') event.preventDefault();
    return openPreferences(root, documentObject, trigger);
  }

  function bind(root, documentObject) {
    if (!documentObject || documentObject.__enigmaCustomerPrivacyBound) return;
    documentObject.__enigmaCustomerPrivacyBound = true;
    customerPrivacyApiFor(root);
    documentObject.addEventListener('click', function(event) {
      handleClick(root, documentObject, event);
    });
  }

  return {
    bind: bind,
    handleClick: handleClick,
    openPreferences: openPreferences,
    privacyBannerFor: privacyBannerFor,
    customerPrivacyApiFor: customerPrivacyApiFor,
    preferencesProcessingAllowed: preferencesProcessingAllowed,
    preferencesProcessingAllowedSync: preferencesProcessingAllowedSync,
    whenPreferencesAllowed: whenPreferencesAllowed
  };
});
